/**
 * Один ключевой показатель (redesign по макету сайдбара): белая карточка
 * с цветной полосой слева (цвет — семантика показателя), лейбл + чип
 * динамики сверху, иконка + значение + единица снизу. Некликабельный (§3 FE-15).
 *
 * Значение опционально: без него карточка показывает название и подпись
 * «нет данных» — без иконки, единицы и чипа динамики (замечание ревью от
 * 14.08.2026) — так сайдбар держит стабильный набор карточек даже без
 * данных по КАТО.
 */
import { TrendingDown, TrendingUp } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '../../../lib/cn';

type Accent = 'green' | 'blue' | 'danger';

interface IndicatorProps {
  icon: LucideIcon;
  label: string;
  /** null/undefined — данных нет, рендерятся только название и иконка. */
  value?: string | number | null;
  unit?: string;
  /** Цвет полосы-акцента; danger дополнительно тонирует карточку и значение. */
  accent?: Accent;
  /**
   * Динамика к прошлому периоду в п.п. (чип «↗ +1.2» на макете).
   * В API пока нет — чип появится, когда данные начнут приходить.
   */
  trend?: number | null;
  testId?: string;
}

const accentBar: Record<Accent, string> = {
  green: 'border-l-brand-500',
  blue: 'border-l-status-info',
  danger: 'border-l-status-danger',
};

export const Indicator = ({
  icon: Icon,
  label,
  value,
  unit,
  accent = 'green',
  trend,
  testId,
}: IndicatorProps) => {
  const { t } = useTranslation();
  const danger = accent === 'danger';
  const noData = value == null;
  const TrendIcon = trend != null && trend < 0 ? TrendingDown : TrendingUp;
  return (
    <div
      className={cn(
        'rounded-2xl border-l-[5px] px-4 py-3.5 shadow-[0_1px_4px_rgba(0,0,0,0.06)]',
        accentBar[accent],
        danger ? 'bg-[#FDF1EE]' : 'bg-white'
      )}
      data-testid={testId ?? 'indicator'}
    >
      <div className="flex items-center justify-between gap-1.5">
        <span className="truncate text-[14px] font-semibold text-text">
          {label}
        </span>
        {!noData && trend != null && (
          <span
            className={cn(
              'flex shrink-0 items-center gap-1 rounded-full border border-surface-border bg-white px-2 py-0.5 text-[12px] font-bold tabular-nums',
              trend < 0 ? 'text-status-danger' : 'text-brand-600'
            )}
            data-testid="indicator-trend"
          >
            <TrendIcon className="size-3.5" />
            {trend > 0 ? `+${trend}` : trend}
          </span>
        )}
      </div>
      {noData ? (
        // Без данных: только название + «нет данных», без иконки и единицы.
        <p
          className="mt-2.5 text-sm lowercase text-text-muted"
          data-testid="indicator-nodata"
        >
          {t('common.noData')}
        </p>
      ) : (
        <div className="mt-2.5 flex items-center gap-2.5">
          <Icon
            strokeWidth={1.7}
            className={cn(
              'size-[22px] shrink-0',
              danger ? 'text-status-danger' : 'text-text-muted'
            )}
          />
          <p
            className={cn(
              'text-[25px] font-extrabold leading-none tabular-nums',
              danger ? 'text-status-danger' : 'text-text'
            )}
            data-testid="indicator-value"
          >
            {value}
          </p>
          {unit && (
            <p
              className={cn(
                'text-[13px]',
                danger ? 'text-status-danger' : 'text-text-faint'
              )}
            >
              {unit}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
